import { memo, useMemo } from 'react';
import PropTypes from 'prop-types';

import Badge from '../../../Badge.jsx';
import { toneFor } from '../constants/statTones';
import { slotCells } from '../shared';

/**
 * One item, read out of its slots.
 *
 * Every slot is drawn the way its name says it should be, and the differences
 * are the point:
 *
 * - **`identity`** is the heading. Its first cell is the name and carries the
 *   weight; the rest sit beside it, muted. Joined at equal weight —
 *   `ABC-123 · Mazda 3 · 2021` — the plate stops being the thing you scan for.
 * - **`status`** is a badge. It is a state, and a state is read by shape.
 * - **`fact`** is label *and* value. `48,210 km · Aug 14, 2026` tells a reader
 *   nothing about which is the mileage and which the last service; the label
 *   is not decoration when two facts share a row.
 * - **`signal`** is a figure in its own column at the right. It is the number
 *   the list is scanned down — open repairs, days overdue — and a column is
 *   how a reader compares. It used to be an amber badge, which announces
 *   trouble even when the value is zero.
 * - **`context`** is where the record sits — the owner, the branch. Muted, and
 *   last, because it qualifies the item rather than describing it. It was
 *   simply dropped before.
 */
const toItem = (entry, index) => {
  const identity = slotCells(entry.slots, 'identity');
  const signal = slotCells(entry.slots, 'signal');

  return {
    key: entry.row_key ?? entry.id ?? String(index),
    name: identity[0]?.text ?? entry.label ?? '',
    qualifier: identity.slice(1).map((cell) => cell.text).join(' · '),
    badges: slotCells(entry.slots, 'status'),
    facts: slotCells(entry.slots, 'fact'),
    context: slotCells(entry.slots, 'context'),
    signal: signal[0] ?? null,
    raw: entry,
  };
};

/** The pre-v2 shape: a flat label plus one status. */
const toLegacyItem = (item) => ({
  key: item.id,
  name: item.label,
  qualifier: '',
  badges: item.status ? [{ key: 'status', text: item.status, tone: item.tone }] : [],
  facts: [],
  context: [],
  signal: null,
  raw: item,
});

/**
 * Scannable items with one status each.
 *
 * The status is what separates this from `ConnectionsWidget`: a list item is
 * a thing in a state, a connection is a thing you are related to. Rendering a
 * relationship as a status badge is how "Account owner" ends up looking like
 * something that needs attention.
 *
 * ## Reading the contract (CT-5641)
 *
 * `LIST` does not publish a label and a status. It publishes each item's
 * fields already sorted into named **slots** — `identity`, `status`, `signal`,
 * `fact`, `context`, `source` — and the slot is the instruction for how to
 * draw the field. See `recordSlots.js` for the table, and `toItem` above for
 * what each one becomes here.
 *
 * That is what lets one component list vehicles, customers and repair orders
 * without knowing anything about any of them: the author bound `plate` to
 * `identity` and `state` to `status`, and the drawing follows. A flat
 * `{label, status}` could only ever render what someone had already decided
 * was a label.
 *
 * @param {object} props
 * @param {{ items: object[] }} props.data `LIST`'s `items[]`, or the pre-v2 flat items.
 * @param {(item: object) => void} [props.onItemClick] Called with the raw item when a row is activated.
 * @param {string} [props.className]
 */
function ListWidget({ data, onItemClick, className = '' }) {
  const items = useMemo(() => (data?.items ?? []).map(
    (item, index) => (item.slots ? toItem(item, index) : toLegacyItem(item)),
  ), [data]);

  if (!items.length) return null;

  return (
    <ul className={`m-0 flex list-none flex-col p-0 ${className}`.trim()}>
      {items.map((item) => {
        const row = (
          <>
            <span className="flex min-w-0 flex-1 flex-col gap-0.5">
              <span className="flex min-w-0 flex-wrap items-baseline gap-1.5">
                <span className="truncate text-xs font-semibold text-content">{item.name}</span>
                {item.qualifier ? (
                  <span className="truncate text-[11px] text-content-secondary">
                    {item.qualifier}
                  </span>
                ) : null}
                {item.badges.map((badge) => (
                  <Badge key={badge.key} size="small" variant={toneFor(badge.tone).badge}>
                    {badge.text}
                  </Badge>
                ))}
              </span>

              {item.facts.length ? (
                <span className="flex min-w-0 flex-wrap gap-x-3 gap-y-0.5">
                  {item.facts.map((fact) => (
                    <span key={fact.key} className="flex items-baseline gap-1 text-[11px]">
                      <span className="text-content-faint">{fact.label}</span>
                      <span className="text-content-muted">{fact.text}</span>
                    </span>
                  ))}
                </span>
              ) : null}

              {item.context.length ? (
                <span className="truncate text-[10px] text-content-faint">
                  {item.context.map((cell) => `${cell.label} ${cell.text}`).join(' · ')}
                </span>
              ) : null}
            </span>

            {item.signal ? (
              <span className="flex w-16 shrink-0 flex-col items-end">
                <span className="text-sm font-semibold leading-none text-content">
                  {item.signal.text}
                </span>
                <span className="truncate text-[10px] leading-tight text-content-faint">
                  {item.signal.label}
                </span>
              </span>
            ) : null}
          </>
        );

        return (
          <li
            key={item.key}
            className="flex items-start gap-2 border-b border-edge py-2 last:border-b-0"
          >
            {onItemClick ? (
              <button
                type="button"
                onClick={() => onItemClick(item.raw)}
                className="flex min-w-0 flex-1 items-start gap-2 rounded text-left hover:text-content-hover"
              >
                {row}
              </button>
            ) : (
              row
            )}
          </li>
        );
      })}
    </ul>
  );
}

ListWidget.propTypes = {
  /** `LIST`'s `items[{row_key, slots}]`. Pre-v2 `{id, label, status, tone}` still renders. */
  data: PropTypes.shape({
    items: PropTypes.arrayOf(
      PropTypes.shape({
        row_key: PropTypes.string,
        slots: PropTypes.object,
        // pre-v2
        id: PropTypes.string,
        label: PropTypes.string,
        status: PropTypes.string,
        tone: PropTypes.oneOf(['neutral', 'info', 'success', 'warning', 'critical']),
      }),
    ).isRequired,
  }).isRequired,
  onItemClick: PropTypes.func,
  className: PropTypes.string,
};

export default memo(ListWidget);
